/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import apiService from "services/apiService";
import { PROFILE_HEADER_ACTIONS_SLOT_ID } from "./headerActionsSlot";
import { colors } from "themes";
import CommonListingFilter, {
    CommonFilterField,
} from "components/CommonListingFilter/CommonListingFilter";
import Timeline from "components/Timeline";

/* ────────────────────────────────────────────────────────────
   TYPES
──────────────────────────────────────────────────────────── */
interface Top5App {
    applicationName: string;
    duration: number;
    usagePercentage: number;
}

interface Top5Url {
    domain: string;
    duration: number;
    usagePercentage: number;
}

interface TimelineSegment {
    status: "ACTIVE" | "IDLE" | "OFFLINE" | string;
    start: string;
    end: string;
    durationSeconds: number;
    sessionCount: number;
}

interface DayData {
    date: string;
    firstActivity: string | null;
    lastActivity: string | null;
    totalTime: number;
    activeTime: number;
    idleTime: number;
    breakTime: number;
    offlineTime: number;
    sessionCount: number;
    segments: TimelineSegment[];
}

interface SessionSummaryResponse {
    totalTime: number;
    sessionCount: number;
    activeTime: number;
    activePercentage: number;
    idleTime: number;
    idlePercentage: number;
    breakTime: number;
    breakPercentage: number;
    uniqueApplications: number;
    top5AppsDisplay: Top5App[];
    top5UrlsDisplay?: Top5Url[];
    offlineTime: number;
    days: DayData[];
}

interface DateFilters {
    fromDate: string;
    toDate: string;
}
interface FilterErrors {
    fromDate?: string;
    toDate?: string;
}

/* ────────────────────────────────────────────────────────────
   HELPERS
──────────────────────────────────────────────────────────── */
const toISODate = (d: Date) => d.toISOString().slice(0, 10);
const defaultRange = (): DateFilters => {
    const today = new Date();
    const weekAgo = new Date();
    weekAgo.setDate(today.getDate() - 7);
    return { fromDate: toISODate(weekAgo), toDate: toISODate(today) };
};

const formatDuration = (secs: number | null | undefined) => {
    if (!secs || secs <= 0) return "0m";
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
};

const appDisplayName = (exeName: string) => exeName.replace(/\.exe$/i, "");
const PRIMARY = colors.primary || "#2563eb";

interface AppLogoStyle {
    bg: string;
    fg: string;
    mark: string;
}
const APP_LOGO_MAP: { test: RegExp; style: AppLogoStyle }[] = [
    { test: /code|vscode/i, style: { bg: "#e0edff", fg: "#2563eb", mark: "VS" } },
    { test: /chrome/i, style: { bg: "#fef2e0", fg: "#ea8600", mark: "Ch" } },
    { test: /firefox/i, style: { bg: "#ffe8e0", fg: "#ff6611", mark: "Fx" } },
    { test: /edge/i, style: { bg: "#e0f7ee", fg: "#0d9d58", mark: "Ed" } },
    { test: /teams/i, style: { bg: "#e5e0ff", fg: "#5b5fc7", mark: "Tm" } },
    { test: /slack/i, style: { bg: "#f3e0ff", fg: "#8b2f8f", mark: "Sl" } },
    { test: /photoshop|^ps/i, style: { bg: "#dbe9ff", fg: "#1e3a8a", mark: "Ps" } },
    { test: /illustrator/i, style: { bg: "#fde9d7", fg: "#c2410c", mark: "Ai" } },
    { test: /figma/i, style: { bg: "#f2e0ff", fg: "#7c3aed", mark: "Fi" } },
    { test: /idea|intellij/i, style: { bg: "#ffe6e6", fg: "#dc2626", mark: "IJ" } },
    { test: /terminal|powershell|cmd/i, style: { bg: "#e5e5e5", fg: "#374151", mark: ">_" } },
    { test: /outlook/i, style: { bg: "#dbeafe", fg: "#1d4ed8", mark: "Ou" } },
    { test: /excel/i, style: { bg: "#dcfce7", fg: "#15803d", mark: "Xl" } },
    { test: /word/i, style: { bg: "#dbeafe", fg: "#1d4ed8", mark: "Wd" } },
    { test: /zoom/i, style: { bg: "#dbeafe", fg: "#2563eb", mark: "Zm" } },
    { test: /notion/i, style: { bg: "#e5e5e5", fg: "#111827", mark: "No" } },
    { test: /explorer/i, style: { bg: "#fef3c7", fg: "#b45309", mark: "Ex" } },
];

const getAppLogoStyle = (exeName: string): AppLogoStyle => {
    const found = APP_LOGO_MAP.find((entry) => entry.test.test(exeName));
    if (found) return found.style;
    const clean = exeName.replace(/\.exe$/i, "");
    return { bg: "#dbeafe", fg: "#2563eb", mark: clean.slice(0, 2).toUpperCase() };
};

const getUrlLogoStyle = (domain: string): AppLogoStyle => {
    const clean = domain.replace(/^www\./i, "");
    let mark = clean.slice(0, 2).toUpperCase();

    if (clean.includes("onedrive")) mark = "OD";
    if (clean.includes("localhost")) mark = "LH";
    if (clean.includes("lirisoft") || clean.includes("liriworks")) mark = "LW";

    return { bg: "#e0f2fe", fg: "#0ea5e9", mark };
};

/* ────────────────────────────────────────────────────────────
   ICONS
──────────────────────────────────────────────────────────── */
const ClockIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const TrendIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
);
const CalendarIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
);
const CoffeeIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18 8h1a4 4 0 010 8h-1M18 8H4a4 4 0 004 4v1a4 4 0 004 4h2a4 4 0 004-4v-1a4 4 0 00-4-4z" /></svg>
);
const LeaveIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
);
const AssetIcon = () => (
    <svg className="w-[18px] h-[18px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
);
const BulbIcon = () => (
    <svg className="w-[16px] h-[16px]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 18h6M10 21h4M12 3a6 6 0 00-4 10.472V16a1 1 0 001 1h6a1 1 0 001-1v-2.528A6 6 0 0012 3z" /></svg>
);
const InfoIcon = () => (
    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const AwardIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15a4 4 0 100-8 4 4 0 000 8zM6 20l1.5-5M18 20l-1.5-5" /></svg>
);
const StarIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.977 2.89a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.89a1 1 0 00-1.176 0l-3.976 2.89c-.783.57-1.838-.196-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.977-2.89c-.783-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" /></svg>
);
const DownloadIcon = () => (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" /></svg>
);
const GlobeIcon = () => (
    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
);
const ExternalLinkIcon = () => (
    <svg className="w-3.5 h-3.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
);

/* ────────────────────────────────────────────────────────────
   COMPONENTS
──────────────────────────────────────────────────────────── */
const StatCard = ({
    icon,
    iconBg,
    iconColor,
    label,
    value,
}: {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    label: string;
    value: React.ReactNode;
}) => (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm px-3 py-3 flex items-center gap-2.5">
        <div
            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
            style={{ backgroundColor: iconBg, color: iconColor }}
        >
            {icon}
        </div>
        <div className="min-w-0">
            <p className="text-[12px] font-medium text-gray-500 dark:text-gray-400 leading-tight truncate">
                {label}
            </p>
            <p className="text-[18px] font-bold text-gray-900 dark:text-white leading-tight truncate">
                {value}
            </p>
        </div>
    </div>
);

/* ────────────────────────────────────────────────────────────
   MAIN COMPONENT
──────────────────────────────────────────────────────────── */
interface EmployeeWorkSummaryProps {
    user: any;
}

export default function EmployeeWorkSummary({ user }: EmployeeWorkSummaryProps) {
    const employeeId = user.id;

    const [dataset, setDataset] = useState<SessionSummaryResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [filters, setFilters] = useState<DateFilters>(defaultRange());
    const [localFilters, setLocalFilters] = useState<DateFilters>(defaultRange());
    const [filterErrors, setFilterErrors] = useState<FilterErrors>({});

    const updateLocalFilter = (key: keyof DateFilters, value: string) => {
        setLocalFilters((prev) => {
            const next = { ...prev, [key]: value };
            if (key === "fromDate" && !value) next.toDate = "";
            return next;
        });
        setFilterErrors((prev) => ({ ...prev, [key]: undefined }));
    };

    const handleApplyFilters = () => {
        const errors: FilterErrors = {};
        let hasError = false;
        if (!localFilters.fromDate) { errors.fromDate = "Start Date is required"; hasError = true; }
        if (!localFilters.toDate) { errors.toDate = "End Date is required"; hasError = true; }
        setFilterErrors(errors);
        if (hasError) return false;
        setFilters(localFilters);
        return true;
    };

    const handleClearFilters = () => {
        setLocalFilters((prev) => ({ ...prev, fromDate: "", toDate: "" }));
        setFilterErrors({});
    };

    const handleResetFilters = () => {
        const reset = defaultRange();
        setLocalFilters(reset);
        setFilterErrors({});
        setFilters(reset);
    };

    const dateFilterFields: CommonFilterField[] = [
        {
            id: "employee-work-summary-from-date",
            type: "date",
            label: "From Date",
            widthClass: "min-w-[170px]",
            queryParam: "fromDate",
            value: localFilters.fromDate,
            max: localFilters.toDate || undefined,
            onChange: (value) => updateLocalFilter("fromDate", value),
            isRequired: true,
            errorMessage: filterErrors.fromDate,
        },
        {
            id: "employee-work-summary-to-date",
            type: "date",
            label: "To Date",
            widthClass: "min-w-[170px]",
            queryParam: "toDate",
            value: localFilters.toDate,
            min: localFilters.fromDate || undefined,
            onChange: (value) => updateLocalFilter("toDate", value),
            isRequired: true,
            errorMessage: filterErrors.toDate,
        },
    ];

    const fetchAnalytics = useCallback(async () => {
        if (!employeeId) return;
        setLoading(true);
        setError(null);
        try {
            const res = await apiService.get(
                `api/analytics/session-summary/${employeeId}?fromDate=${filters.fromDate}&toDate=${filters.toDate}`
            );
            setDataset(res?.data || null);
        } catch (err) {
            console.error("Failed to fetch session summary", err);
            setError("Failed to load work summary");
        } finally {
            setLoading(false);
        }
    }, [employeeId, filters]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const [actionsSlot, setActionsSlot] = useState<HTMLElement | null>(null);

    useLayoutEffect(() => {
        setActionsSlot(document.getElementById(PROFILE_HEADER_ACTIONS_SLOT_ID));
    }, []);

    const handleExportReport = () => {
        console.log("Export report clicked", { employeeId, filters });
    };

    const actionsGroup = (
        <div className="flex items-center gap-3 ml-auto">
            <CommonListingFilter
                className="[&>button]:h-9 [&>button]:px-4 [&>button]:rounded-lg [&>button]:border [&>button]:border-gray-200 dark:[&>button]:border-gray-700 [&>button]:bg-white dark:[&>button]:bg-gray-800"
                fields={dateFilterFields}
                panelTitle="Filter by Date Range"
                panelAlign="right"
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
                onReset={handleResetFilters}
            />
            <button
                onClick={handleExportReport}
                style={{ backgroundColor: PRIMARY }}
                className="flex items-center gap-2 h-9 px-4 rounded-lg text-white text-sm font-medium hover:opacity-90 transition-opacity shrink-0"
            >
                <DownloadIcon />
                Export Report
            </button>
        </div>
    );

    const headerRow = actionsSlot ? (
        createPortal(actionsGroup, actionsSlot)
    ) : (
        <div className="flex flex-wrap items-start justify-end w-full gap-3 pt-2">{actionsGroup}</div>
    );

    if (error && !loading) {
        return (
            <div className="space-y-3">
                {headerRow}
                <div className="flex flex-col items-center justify-center py-10 text-center">
                    <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>
                    <button
                        onClick={fetchAnalytics}
                        style={{ backgroundColor: PRIMARY }}
                        className="px-4 py-1.5 text-white text-sm rounded-md hover:opacity-90"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (loading || !dataset) {
        return (
            <div className="space-y-3">
                {headerRow}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 animate-pulse">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="h-16 rounded-xl bg-gray-100 dark:bg-gray-800" />
                    ))}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 animate-pulse">
                    <div className="h-52 rounded-xl bg-gray-100 dark:bg-gray-800" />
                    <div className="h-52 rounded-xl bg-gray-100 dark:bg-gray-800" />
                    <div className="h-52 rounded-xl bg-gray-100 dark:bg-gray-800" />
                </div>
            </div>
        );
    }

    const donutSlices = [
        { label: "Focused Time", value: formatDuration(dataset.activeTime), percent: dataset.activePercentage, color: PRIMARY },
        { label: "Break Time", value: formatDuration(dataset.breakTime), percent: dataset.breakPercentage, color: "#60a5fa" },
        { label: "Idle Time", value: formatDuration(dataset.idleTime), percent: dataset.idlePercentage, color: "#d1d5db" },
    ];

    const svgSize = 112;
    const strokeWidth = 11;
    const radius = (svgSize - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const gapInPixels = 3;
    const validSlices = donutSlices.filter((s) => s.percent > 0);
    let currentOffset = 0;

    return (
        <div className="space-y-2">
            {headerRow}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 pt-2">
                <StatCard
                    icon={<ClockIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Total Work Time"
                    value={formatDuration(dataset.totalTime)}
                />
                <StatCard
                    icon={<TrendIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Productivity"
                    value={`${dataset.activePercentage.toFixed(1)}%`}
                />
                <StatCard
                    icon={<CalendarIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Sessions"
                    value={dataset.sessionCount}
                />
                <StatCard
                    icon={<CoffeeIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Idle Time"
                    value={formatDuration(dataset.idleTime)}
                />
                <StatCard
                    icon={<LeaveIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Leaves"
                    value="2/10"
                />
                <StatCard
                    icon={<AssetIcon />}
                    iconBg="#eff6ff"
                    iconColor={PRIMARY}
                    label="Total Assets"
                    value="4"
                />
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm py-3 grid grid-cols-2 lg:grid-cols-4 gap-y-3 lg:divide-x divide-gray-300 dark:divide-gray-600">
                <div className="flex items-center gap-2 px-5 text-sm font-semibold lg:justify-center" style={{ color: PRIMARY }}>
                    <BulbIcon />
                    Insights for you
                </div>

                <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                        <span style={{ color: PRIMARY }}>
                            <AwardIcon />
                        </span>
                    </div>
                    <div className="text-[13px] leading-tight">
                        <p className="text-gray-500 dark:text-gray-400">Applications used</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{dataset.uniqueApplications} apps</p>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                    <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                        <span style={{ color: PRIMARY }}>
                            <TrendIcon />
                        </span>
                    </div>
                    <div className="text-[13px] leading-tight">
                        <p className="text-gray-500 dark:text-gray-400">You stayed focused for</p>
                        <p className="font-semibold text-gray-900 dark:text-white">
                            {dataset.activePercentage.toFixed(0)}% of tracked time
                        </p>
                    </div>
                </div>

                {dataset.top5AppsDisplay?.[0] && (
                    <div className="flex items-center gap-2.5 px-5 lg:justify-center">
                        <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-gray-700 flex items-center justify-center shrink-0">
                            <span style={{ color: PRIMARY }}>
                                <StarIcon />
                            </span>
                        </div>
                        <div className="text-[13px] leading-tight">
                            <p className="text-gray-500 dark:text-gray-400">Most used app</p>
                            <p className="font-semibold text-gray-900 dark:text-white">
                                {appDisplayName(dataset.top5AppsDisplay[0].applicationName)}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                    <div className="flex items-center gap-1.5 mb-3">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white">Where your time went</h3>
                        <span className="text-gray-400">
                            <InfoIcon />
                        </span>
                    </div>
                    <div className="flex items-center gap-5">
                        <div className="relative shrink-0" style={{ width: svgSize, height: svgSize }}>
                            <svg width={svgSize} height={svgSize} className="-rotate-90">
                                <circle
                                    cx={svgSize / 2}
                                    cy={svgSize / 2}
                                    r={radius}
                                    fill="none"
                                    className="stroke-gray-100 dark:stroke-gray-700"
                                    strokeWidth={strokeWidth}
                                />
                                {validSlices.map((s, i) => {
                                    const sliceLength = (s.percent / 100) * circumference;
                                    const dashLength = Math.max(0, sliceLength - (validSlices.length > 1 ? gapInPixels : 0));

                                    const element = (
                                        <circle
                                            key={i}
                                            cx={svgSize / 2}
                                            cy={svgSize / 2}
                                            r={radius}
                                            fill="none"
                                            stroke={s.color}
                                            strokeWidth={strokeWidth}
                                            strokeDasharray={`${dashLength} ${circumference}`}
                                            strokeDashoffset={-currentOffset}
                                        />
                                    );

                                    currentOffset += sliceLength;
                                    return element;
                                })}
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center">
                                <span className="text-[11px] text-gray-400 leading-none">Total</span>
                                <span className="text-base font-bold text-gray-900 dark:text-white mt-1 leading-none">
                                    {formatDuration(dataset.totalTime)}
                                </span>
                            </div>
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                            {donutSlices.map((s, i) => (
                                <div key={i} className="flex items-center justify-between gap-2 text-[13px]">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                                        <span className="text-gray-600 dark:text-gray-300 font-medium truncate">{s.label}</span>
                                    </div>
                                    <div className="flex items-center gap-2 shrink-0">
                                        <span className="font-semibold text-gray-900 dark:text-white">{s.value}</span>
                                        <span className="text-gray-400 w-9 text-right">{s.percent.toFixed(0)}%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    {dataset.activePercentage > 0 && (
                        <div className="mt-auto pt-3">
                            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg px-3 py-2 flex items-center gap-2 text-[12.5px] font-small text-blue-700 dark:text-blue-400">
                                <AwardIcon />
                                Great focus! You spent {dataset.activePercentage.toFixed(0)}% of your time on focused work.
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white">Top Applications</h3>
                    </div>
                    {!dataset.top5AppsDisplay || dataset.top5AppsDisplay.length === 0 ? (
                        <p className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                            No application data available.
                        </p>
                    ) : (
                            <div className="space-y-2.5 max-h-[190px] overflow-y-auto pr-2">
                            {dataset.top5AppsDisplay.map((app, i) => {
                                const logo = getAppLogoStyle(app.applicationName);
                                return (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="text-[12px] font-medium text-gray-400 w-3">{i + 1}</span>
                                        <div
                                            className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0"
                                            style={{ backgroundColor: logo.bg, color: logo.fg }}
                                        >
                                            {logo.mark}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between text-[13px] mb-1">
                                                <span className="text-gray-800 font-medium dark:text-gray-200 truncate">
                                                    {appDisplayName(app.applicationName)}
                                                </span>
                                                <div className="flex items-center gap-3 shrink-0 ml-2">
                                                    <span className="font-semibold text-gray-900 dark:text-white">
                                                        {app.usagePercentage.toFixed(1)}%
                                                    </span>
                                                    <span className="text-gray-400 w-16 text-right whitespace-nowrap">
                                                        {formatDuration(app.duration)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{ width: `${app.usagePercentage}%`, backgroundColor: PRIMARY }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex flex-col">
                    <div className="flex items-center gap-1.5 mb-3">
                        <h3 className="font-semibold text-base text-gray-900 dark:text-white">Top URLs</h3>
                        <GlobeIcon />
                    </div>
                    {!dataset.top5UrlsDisplay || dataset.top5UrlsDisplay.length === 0 ? (
                        <p className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
                            No url data available right now.
                        </p>
                    ) : (
                            <div className="space-y-2.5 max-h-[190px] overflow-y-auto pr-2">
                            {dataset.top5UrlsDisplay.map((urlItem, i) => {
                                const logo = getUrlLogoStyle(urlItem.domain);
                                return (
                                    <div key={i} className="flex items-center gap-3">
                                        <span className="text-[12px] font-medium text-gray-400 w-3">{i + 1}</span>
                                        <div
                                            className="w-7 h-7 rounded-md flex items-center justify-center text-[11px] font-bold shrink-0"
                                            style={{ backgroundColor: logo.bg, color: logo.fg }}
                                        >
                                            {logo.mark}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between text-[13px] mb-1">
                                                <a
                                                    href={`https://${urlItem.domain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="group flex items-center gap-1 min-w-0 text-gray-800 font-medium dark:text-gray-200 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                                                    title={urlItem.domain}
                                                >
                                                    <span className="truncate">{urlItem.domain}</span>
                                                    <ExternalLinkIcon />
                                                </a>
                                                <div className="flex items-center gap-3 shrink-0 ml-2">
                                                    <span className="font-semibold text-gray-900 dark:text-white">
                                                        {urlItem.usagePercentage.toFixed(1)}%
                                                    </span>
                                                    <span className="text-gray-400 w-16 text-right whitespace-nowrap">
                                                        {formatDuration(urlItem.duration)}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full rounded-full"
                                                    style={{ width: `${urlItem.usagePercentage}%`, backgroundColor: PRIMARY }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {dataset.days && dataset.days.length > 0 && (
                <Timeline
                    days={dataset.days}
                    subtitle="Active, idle and offline periods plotted on a shared clock, with holidays and leaves."
                    formatDuration={formatDuration}
                    email={user?.email}
                />
            )}
        </div>
    );
}